/**
 * Composant pour créer des parcours d'évaluation
 *
 * Ce composant permet de créer des parcours avec:
 * - Sélection du type de parcours via menu déroulant
 * - Personnalisation optionnelle du type et template de chaque évaluation
 */

import { useCreerParcours } from "@/lib/creerParcours";
import {
  PARCOURS_TYPES_RESTRICTED,
  getFilteredParcoursOptions,
  getParcoursConfig,
  getTemplatesForType,
  getTypeEvalLabel,
  type EvalConfig,
  type TypeParcoursKey,
} from "@/lib/parcoursConfig";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@repo/ui";
import { ChevronDown, ChevronUp, RefreshCw } from "lucide-react";
import { useRef, useState, type FormEvent } from "react";
import { buildParcoursDescription } from "@repo/utils";
import { Link } from "react-router";
import {
  TemplateEntStructEnum,
  TemplateFormEnum,
  TemplateInbasketEnum,
  TypeEvalEnum,
} from "../../../../convex/schema";

interface ParcoursResult {
  parcoursId: string;
  evaluationsIds: string[];
}

interface CandidatInfo {
  emailCandidat: string;
  nomCandidat: string;
  prenomCandidat: string;
  token?: string;
  anonyme?: boolean;
}

const parcoursOptions = getFilteredParcoursOptions();
const defaultParcoursType = (parcoursOptions[0]?.value ?? "evaluation_manageriale") as TypeParcoursKey;
const hideParcoursTypeSelector =
  parcoursOptions.length === 1 &&
  getParcoursConfig(defaultParcoursType).evaluations.every((e) => !!e.template);

export function ParcoursCreator() {
  const [parcoursType, setParcoursType] = useState<TypeParcoursKey>(
    defaultParcoursType,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<ParcoursResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const successRef = useRef<HTMLDivElement>(null);

  // État anonymisation
  const [anonyme, setAnonyme] = useState(false);
  const [token, setToken] = useState("");

  // État pour la personnalisation
  const [isCustomizing, setIsCustomizing] = useState(false);
  const [customEvaluations, setCustomEvaluations] = useState<EvalConfig[]>(
    () => [...getParcoursConfig(defaultParcoursType).evaluations],
  );

  // Hook pour créer le parcours
  const creerParcours = useCreerParcours();

  const genererToken = () => {
    const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
    const part = (n: number) =>
      Array.from({ length: n }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
    setToken(`${part(4)}-${part(4)}`);
  };

  // Initialiser les évaluations personnalisées quand le type de parcours change
  const handleParcoursTypeChange = (value: TypeParcoursKey) => {
    setParcoursType(value);
    const config = getParcoursConfig(value);
    setCustomEvaluations([...config.evaluations]);
    setIsCustomizing(false);
  };

  // Modifier le type d'une évaluation
  const handleTypeEvalChange = (index: number, newType: TypeEvalEnum) => {
    const templates = getTemplatesForType(newType);
    const defaultTemplate = templates[0] as
      | TemplateFormEnum
      | TemplateInbasketEnum
      | TemplateEntStructEnum
      | undefined;

    setCustomEvaluations((prev) => {
      const updated = [...prev];
      updated[index] = {
        typeEval: newType,
        template: defaultTemplate,
      };
      return updated;
    });
  };

  // Modifier le template d'une évaluation
  const handleTemplateChange = (
    index: number,
    template: TemplateFormEnum | TemplateInbasketEnum | TemplateEntStructEnum,
  ) => {
    setIsCustomizing(true);
    setCustomEvaluations((prev) => {
      const updated = [...prev];
      const currentEval = updated[index];
      if (currentEval) {
        updated[index] = {
          typeEval: currentEval.typeEval,
          template,
        };
      }
      return updated;
    });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setResult(null);

    try {
      const formData = new FormData(e.currentTarget);
      const candidat: CandidatInfo = {
        emailCandidat: anonyme ? "" : (formData.get("email") as string),
        nomCandidat: anonyme ? token : (formData.get("nom") as string),
        prenomCandidat: anonyme ? "" : (formData.get("prenom") as string),
        token: anonyme ? token : undefined,
        anonyme,
      };

      // Construire la configuration à utiliser
      const baseConfig = getParcoursConfig(parcoursType);
      const configToUse = {
        ...baseConfig,
        evaluations: isCustomizing ? customEvaluations : baseConfig.evaluations,
      };

      const response = await creerParcours(candidat, configToUse);

      if (response) {
        setResult({
          parcoursId: response.parcoursId,
          evaluationsIds: response.evaluationsIds.map((id) => id.toString()),
        });
        setTimeout(() => {
          successRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 50);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsLoading(false);
    }
  };

  const currentConfig = getParcoursConfig(parcoursType);

  return (
    <div className="w-full min-w-[33%] px-8 py-8">
      <div className="mb-6">
        <Link to="/" className="text-sm text-muted-foreground hover:underline">
          ← Retour à la liste
        </Link>
      </div>

      <div className="grid gap-6 w-7xl">
        {/* Résultat */}
        {result && (
          <Card ref={successRef} className="border-green-500">
            <CardHeader>
              <CardTitle className="text-green-600">
                Parcours créé avec succès!
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-2 font-mono text-sm">
                <div>
                  <strong>Parcours ID:</strong> {result.parcoursId}
                </div>
                <div>
                  <strong>Évaluations créées:</strong>
                  <ul className="list-disc list-inside ml-2">
                    {result.evaluationsIds.map((id, index) => (
                      <li key={id}>
                        Évaluation {index + 1}: {id}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="mt-4">
                  <Link to={`/p/${result.parcoursId}`}>
                    <Button variant="outline">Voir le parcours</Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Erreur */}
        {error && (
          <Card className="border-red-500">
            <CardHeader>
              <CardTitle className="text-red-600">Erreur</CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="text-sm text-red-600 whitespace-pre-wrap">
                {error}
              </pre>
            </CardContent>
          </Card>
        )}

        <Card className="min-w-112.5">
          <CardHeader>
            <CardTitle>Créer un Parcours d'Évaluation</CardTitle>
            <CardDescription>
              Remplissez les informations du candidat et sélectionnez le type de
              parcours à créer.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="grid gap-4">
              {/* Mode anonyme */}
              <div className="flex items-center gap-2">
                <input
                  id="anonyme"
                  type="checkbox"
                  checked={anonyme}
                  onChange={(e) => { setAnonyme(e.target.checked); setToken(""); result && setResult(null); }}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label htmlFor="anonyme">Anonymiser ce parcours</Label>
              </div>

              {/* Informations du candidat */}
              {!anonyme && (
                <div className="grid gap-2">
                  <Label htmlFor="nom">Nom du candidat</Label>
                  <Input
                    id="nom"
                    name="nom"
                    type="text"
                    placeholder="Doe"
                    required
                    onChange={() => result && setResult(null)}
                  />
                </div>
              )}

              {!anonyme && (
                <>
                  <div className="grid gap-2">
                    <Label htmlFor="prenom">Prénom du candidat</Label>
                    <Input
                      id="prenom"
                      name="prenom"
                      type="text"
                      placeholder="John"
                      required
                      onChange={() => result && setResult(null)}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="email">Email du candidat</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="john@example.com"
                      required
                      onChange={() => result && setResult(null)}
                    />
                  </div>
                </>
              )}

              {anonyme && (
                <div className="grid gap-2">
                  <Label htmlFor="token-input">Token d'accès candidat</Label>
                  <div className="flex gap-2">
                    <Input
                      id="token-input"
                      type="text"
                      placeholder="ex: abc-1234"
                      value={token}
                      onChange={(e) => setToken(e.target.value)}
                      required
                    />
                    <Button type="button" variant="outline" size="sm" onClick={genererToken} className="shrink-0">
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Sélection du type de parcours */}
              {!hideParcoursTypeSelector && <div className="grid gap-2">
                <Label>Type de parcours</Label>
                <Select
                  value={parcoursType}
                  onValueChange={(value) =>
                    handleParcoursTypeChange(value as TypeParcoursKey)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionnez un type de parcours" />
                  </SelectTrigger>
                  <SelectContent>
                    {parcoursOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground">
                  {buildParcoursDescription(
                    (isCustomizing ? customEvaluations : currentConfig.evaluations).map(
                      (e) => ({ typeEval: e.typeEval, template: e.template }),
                    ),
                  )}
                </p>
              </div>}

              {PARCOURS_TYPES_RESTRICTED ? (
                /* Mode restreint : seules les évaluations sans template fixé sont affichées */
                (() => {
                  const evalsToShow = customEvaluations
                    .map((evalConfig, index) => ({
                      evalConfig,
                      index,
                      hasFixedTemplate: !!currentConfig.evaluations[index]?.template,
                    }))
                    .filter(({ hasFixedTemplate }) => !hasFixedTemplate);

                  if (evalsToShow.length === 0) return null;

                  return (
                    <div className="border rounded-lg p-4 mt-2 space-y-4">
                      <h4 className="font-medium text-sm">
                        Configuration des évaluations
                      </h4>
                      {evalsToShow.map(({ evalConfig, index }) => (
                        <div
                          key={index}
                          className="grid gap-3 p-3 bg-muted/50 rounded-md"
                        >
                          <span className="text-sm font-medium">
                            {getTypeEvalLabel(evalConfig.typeEval)}
                          </span>
                          <div className="grid gap-1">
                            <Label className="text-xs">Template</Label>
                            <Select
                              value={evalConfig.template || ""}
                              onValueChange={(value) =>
                                handleTemplateChange(
                                  index,
                                  value as
                                    | TemplateFormEnum
                                    | TemplateInbasketEnum
                                    | TemplateEntStructEnum,
                                )
                              }
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Sélectionner un template" />
                              </SelectTrigger>
                              <SelectContent>
                                {getTemplatesForType(evalConfig.typeEval).map(
                                  (template) => (
                                    <SelectItem key={template} value={template}>
                                      {template}
                                    </SelectItem>
                                  ),
                                )}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()
              ) : (
                (() => {
                  const evalsToCustomize = customEvaluations
                    .map((evalConfig, index) => ({
                      evalConfig,
                      index,
                      hasFixedTemplate: !!currentConfig.evaluations[index]?.template,
                    }))
                    .filter(({ hasFixedTemplate }) => !hasFixedTemplate);

                  if (evalsToCustomize.length === 0) return null;

                  return (
                    <>
                      {/* Bouton de personnalisation */}
                      <div className="mt-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setIsCustomizing(!isCustomizing)}
                          className="flex items-center gap-2"
                        >
                          {isCustomizing ? (
                            <>
                              <ChevronUp className="h-4 w-4" />
                              Masquer la personnalisation
                            </>
                          ) : (
                            <>
                              <ChevronDown className="h-4 w-4" />
                              Personnaliser le parcours
                            </>
                          )}
                        </Button>
                      </div>

                      {/* Section de personnalisation complète */}
                      {isCustomizing && (
                        <div className="border rounded-lg p-4 mt-2 space-y-4">
                          <h4 className="font-medium text-sm">
                            Personnalisation des évaluations
                          </h4>
                          {evalsToCustomize.map(({ evalConfig, index }) => (
                            <div
                              key={index}
                              className="grid gap-3 p-3 bg-muted/50 rounded-md"
                            >
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium">
                                  Évaluation {index + 1}
                                </span>
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                <div className="grid gap-1">
                                  <Label className="text-xs">Type</Label>
                                  <Select
                                    value={evalConfig.typeEval}
                                    onValueChange={(value) =>
                                      handleTypeEvalChange(
                                        index,
                                        value as TypeEvalEnum,
                                      )
                                    }
                                  >
                                    <SelectTrigger>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {Object.values(TypeEvalEnum).map((type) => (
                                        <SelectItem key={type} value={type}>
                                          {getTypeEvalLabel(type)}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="grid gap-1">
                                  <Label className="text-xs">Template</Label>
                                  <Select
                                    value={evalConfig.template || ""}
                                    onValueChange={(value) =>
                                      handleTemplateChange(
                                        index,
                                        value as
                                          | TemplateFormEnum
                                          | TemplateInbasketEnum
                                          | TemplateEntStructEnum,
                                      )
                                    }
                                  >
                                    <SelectTrigger>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {getTemplatesForType(
                                        evalConfig.typeEval,
                                      ).map((template) => (
                                        <SelectItem key={template} value={template}>
                                          {template}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  );
                })()
              )}

              <Button type="submit" disabled={isLoading || !!result} className="mt-4">
                {isLoading ? "Création en cours..." : "Créer le parcours"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
